import React, { useCallback, useState } from 'react';
import InvoiceDetails from './InvoiceDetails';
import OrderDetails from './OrderDetails';
import { FROM_DATE, TO_DATE } from './constants';
import { fetchJson } from './useSalesInvoices';

// Opens invoices and orders in dialogs. Render `viewer` once in the page; `refreshError` is set when an opened invoice could not be refreshed.
export const useInvoiceViewer = (setInvoices) => {
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [refreshError, setRefreshError] = useState(null);
    // Document number of the invoice currently being refreshed from Fortnox
    const [refreshingInvoice, setRefreshingInvoice] = useState(null);
    // Order opened from an order reference, as { documentNumber, order, error }. Fetched when it is opened.
    const [selectedOrder, setSelectedOrder] = useState(null);

    const closeInvoice = useCallback(() => setSelectedInvoice(null), []);
    const closeOrder = useCallback(() => setSelectedOrder(null), []);

    const openOrder = async (documentNumber) => {
        setSelectedOrder({ documentNumber, order: null, error: null });

        // Ignores the result when another order has been opened or the dialog closed in the meantime
        const update = (changes) => setSelectedOrder((prev) => (prev && prev.documentNumber === documentNumber ? { ...prev, ...changes } : prev));

        try {
            update({ order: await fetchJson(`/api/sales-data/orders/${documentNumber}`) });
        } catch (err) {
            update({ error: err.message });
        }
    };

    // Opens the invoice at once and then replaces it with a version fetched from Fortnox with the cache bypassed
    const openInvoice = async (invoice) => {
        setRefreshError(null);
        setSelectedInvoice(invoice);
        setRefreshingInvoice(invoice.documentNumber);

        try {
            const fresh = await fetchJson(`/api/sales-data/invoices/${invoice.documentNumber}?forceRefresh=true&fromDate=${FROM_DATE}&toDate=${TO_DATE}`);
            setInvoices((prev) => prev.map((i) => (i.documentNumber === fresh.documentNumber ? fresh : i)));
            setSelectedInvoice((prev) => (prev && prev.documentNumber === fresh.documentNumber ? fresh : prev));
        } catch (err) {
            setRefreshError(`Kunde inte uppdatera faktura ${invoice.documentNumber} från Fortnox: ${err.message}`);
        } finally {
            setRefreshingInvoice((prev) => (prev === invoice.documentNumber ? null : prev));
        }
    };

    const viewer = (
        <>
            {selectedInvoice && (
                <InvoiceDetails
                    invoice={selectedInvoice}
                    refreshing={refreshingInvoice === selectedInvoice.documentNumber}
                    suspended={!!selectedOrder}
                    onOpenOrder={openOrder}
                    onClose={closeInvoice}
                />
            )}

            {selectedOrder && (
                <OrderDetails
                    documentNumber={selectedOrder.documentNumber}
                    order={selectedOrder.order}
                    loading={!selectedOrder.order && !selectedOrder.error}
                    error={selectedOrder.error}
                    onClose={closeOrder}
                />
            )}
        </>
    );

    return { openInvoice, openOrder, refreshError, viewer };
};
